import { useQuery } from "@tanstack/react-query";

import type { SkillCollectionVersionRecord } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { parseNdjsonStream } from "@/utils/parseNdjsonStream";

interface GetSkillCollectionVersionResponse {
  success: boolean;
  version: SkillCollectionVersionRecord;
  files: Record<string, string>;
}

type NdjsonLine =
  | { type: "version"; version: SkillCollectionVersionRecord }
  | { type: "file"; path: string; content: string }
  | { type: "error"; path: string; message: string }
  | { type: "done" };

export const useGetSkillCollectionVersion = (
  workspaceId: number | null,
  collectionId: string | null,
  versionNumber: number | null,
) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";

  return useQuery({
    queryKey: ["skill-collection-version", workspaceId, collectionId, versionNumber],
    queryFn: async (): Promise<GetSkillCollectionVersionResponse> => {
      if (!workspaceId || !collectionId || !versionNumber) {
        throw new Error("Missing workspace, collection, or version");
      }

      const res = await fetch(
        ENDPOINTS.skill_collections.version(workspaceId, collectionId, versionNumber),
        {
          headers: jsonAuthHeaders(userToken),
        },
      );

      if (!res.ok) {
        return handleResponse(res);
      }

      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        return handleResponse(res);
      }

      let version: SkillCollectionVersionRecord | null = null;
      const files: Record<string, string> = {};

      for await (const line of parseNdjsonStream<NdjsonLine>(res)) {
        switch (line.type) {
          case "version":
            ({ version } = line);
            break;
          case "file":
            files[line.path] = line.content;
            break;
          case "error":
            files[line.path] = "";
            break;
          default:
            break;
        }
      }

      if (!version) {
        throw new Error("Stream ended without version metadata");
      }

      return { success: true, version, files };
    },
    enabled: !!workspaceId && !!collectionId && !!versionNumber && !!userToken,
  });
};
